import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Mail, Save, Loader2, Bell, AlertCircle, CheckCircle, 
  Settings as SettingsIcon, RefreshCw, Ticket, Plus, X, Users, Clock, RotateCcw,
  ListChecks, ArrowUp, ArrowDown, Pencil, Code2, Copy, StickyNote
} from 'lucide-react';
import { internalStatusBadgeClasses, INTERNAL_STATUS_COLOR_CLASSES } from '../lib/ticketFields';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import RichTextEditor from '../components/RichTextEditor';

import { API_BASE_URL } from '@/lib/apiBase';

const API = API_BASE_URL;

// Internal Status management — add / edit / remove / reorder
const InternalStatusSettings = () => {
  const [items, setItems] = useState([]);
  const [allowedColors, setAllowedColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('slate');
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editColor, setEditColor] = useState('slate');
  const [savingId, setSavingId] = useState(null);

  const fetchItems = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/internal-statuses`);
      setItems(data?.items || []);
      setAllowedColors(data?.allowed_colors || []);
    } catch (e) {
      toast.error('Failed to load internal statuses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const addItem = async () => {
    const label = newLabel.trim();
    if (!label) {
      toast.error('Enter a label');
      return;
    }
    try {
      const { data } = await axios.post(`${API}/settings/internal-statuses`, {
        label,
        color: newColor,
      });
      setItems(data.items);
      setNewLabel('');
      setNewColor('slate');
      toast.success(`Added "${label}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add status');
    }
  };

  const beginEdit = (it) => {
    setEditingId(it.id);
    setEditLabel(it.label);
    setEditColor(it.color);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const label = editLabel.trim();
    if (!label) {
      toast.error('Label cannot be empty');
      return;
    }
    setSavingId(editingId);
    try {
      const { data } = await axios.put(`${API}/settings/internal-statuses/${editingId}`, {
        label,
        color: editColor,
      });
      setItems(data.items);
      setEditingId(null);
      toast.success('Status updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingId(null);
    }
  };

  const deleteItem = async (id, label) => {
    if (!window.confirm(`Remove "${label}"? Tickets using it will lose this value.`)) return;
    setSavingId(id);
    try {
      const { data } = await axios.delete(`${API}/settings/internal-statuses/${id}`);
      setItems(data.items);
      toast.success('Removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    } finally {
      setSavingId(null);
    }
  };

  const move = async (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    setItems(next);
    try {
      await axios.put(`${API}/settings/internal-statuses`, { ids: next.map((i) => i.id) });
    } catch (e) {
      toast.error('Failed to reorder');
      fetchItems();
    }
  };

  return (
    <Card data-testid="internal-status-settings-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-100">
            <ListChecks className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Internal Status Options
            </CardTitle>
            <CardDescription>
              Fine-grained status that supplements the main ticket status. Customers can see these when you update them.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : (
          <>
            <div className="space-y-2" data-testid="internal-status-list">
              {items.map((it, idx) => {
                const isEditing = editingId === it.id;
                return (
                  <div
                    key={it.id}
                    className="rounded-lg border border-slate-200 p-2.5 flex items-center gap-2"
                    data-testid={`internal-status-row-${it.id}`}
                  >
                    <div className="flex flex-col gap-0.5">
                      <button
                        type="button"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0 || savingId === it.id}
                        className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                        data-testid={`internal-status-up-${it.id}`}
                        aria-label="Move up"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(idx, 1)}
                        disabled={idx === items.length - 1 || savingId === it.id}
                        className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                        data-testid={`internal-status-down-${it.id}`}
                        aria-label="Move down"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {isEditing ? (
                      <>
                        <Input
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          className="h-8 flex-1"
                          data-testid={`internal-status-edit-label-${it.id}`}
                        />
                        <ColorPicker
                          value={editColor}
                          onChange={setEditColor}
                          colors={allowedColors.length ? allowedColors : Object.keys(INTERNAL_STATUS_COLOR_CLASSES)}
                        />
                        <Button
                          size="sm"
                          onClick={saveEdit}
                          disabled={savingId === it.id}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white h-8"
                          data-testid={`internal-status-save-${it.id}`}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingId(null)}
                          className="h-8"
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Badge variant="outline" className={`${internalStatusBadgeClasses(it.color)} text-xs`}>
                          {it.label}
                        </Badge>
                        <span className="text-xs text-slate-400 font-mono">{it.id}</span>
                        <div className="ml-auto flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => beginEdit(it)}
                            className="h-8 w-8 p-0"
                            data-testid={`internal-status-edit-${it.id}`}
                            aria-label="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteItem(it.id, it.label)}
                            disabled={savingId === it.id}
                            className="h-8 w-8 p-0 text-rose-500 hover:text-rose-700"
                            data-testid={`internal-status-delete-${it.id}`}
                            aria-label="Delete"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
              {items.length === 0 && (
                <div className="text-sm text-slate-500 text-center py-6">
                  No internal statuses yet — add one below.
                </div>
              )}
            </div>

            {/* Add new */}
            <div className="mt-4 rounded-lg border border-dashed border-slate-300 p-3 flex items-center gap-2" data-testid="internal-status-add-row">
              <Input
                placeholder="New status label (e.g. QA Testing)"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }}
                className="h-9 flex-1"
                data-testid="internal-status-new-label"
              />
              <ColorPicker
                value={newColor}
                onChange={setNewColor}
                colors={allowedColors.length ? allowedColors : Object.keys(INTERNAL_STATUS_COLOR_CLASSES)}
              />
              <Button
                onClick={addItem}
                className="bg-indigo-600 hover:bg-indigo-700 text-white h-9"
                data-testid="internal-status-add-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Small inline color picker used by InternalStatusSettings
const ColorPicker = ({ value, onChange, colors }) => (
  <div className="flex items-center gap-1" data-testid="color-picker">
    {colors.map((c) => (
      <button
        key={c}
        type="button"
        title={c}
        onClick={() => onChange(c)}
        className={`w-5 h-5 rounded-full border ${
          value === c ? 'ring-2 ring-offset-1 ring-slate-400' : ''
        } ${(INTERNAL_STATUS_COLOR_CLASSES[c] || '').split(' ').filter(x => x.startsWith('bg-')).join(' ')}`}
        data-testid={`color-opt-${c}`}
        aria-label={`Color ${c}`}
      />
    ))}
  </div>
);

// SLA Threshold Settings — editable per priority (days/hours/minutes) with pause statuses
const SlaSettings = () => {
  const PRIORITIES = [
    { key: 'critical', label: 'Critical', color: 'bg-rose-50 text-rose-700 border-rose-200', hint: 'Highest urgency' },
    { key: 'high', label: 'High', color: 'bg-orange-50 text-orange-700 border-orange-200', hint: 'Urgent' },
    { key: 'medium', label: 'Medium', color: 'bg-amber-50 text-amber-700 border-amber-200', hint: 'Standard' },
    { key: 'low', label: 'Low', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', hint: 'Non-urgent' },
  ];
  const STATUS_OPTIONS = [
    { key: 'open', label: 'Open' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'waiting_on_customer', label: 'Waiting on Customer' },
    { key: 'under_investigation', label: 'Under Investigation' },
    { key: 'resolved', label: 'Resolved' },
  ];

  const minutesToDhm = (total) => {
    const t = Math.max(0, Number(total) || 0);
    const days = Math.floor(t / (24 * 60));
    const hours = Math.floor((t % (24 * 60)) / 60);
    const minutes = t % 60;
    return { days, hours, minutes };
  };
  const dhmToMinutes = ({ days, hours, minutes }) =>
    (Number(days) || 0) * 24 * 60 + (Number(hours) || 0) * 60 + (Number(minutes) || 0);

  const [durations, setDurations] = useState({
    critical: { days: 0, hours: 4, minutes: 0 },
    high: { days: 1, hours: 0, minutes: 0 },
    medium: { days: 3, hours: 0, minutes: 0 },
    low: { days: 7, hours: 0, minutes: 0 },
  });
  const [defaults, setDefaults] = useState({
    critical: 240, high: 1440, medium: 4320, low: 10080,
  });
  const [pauseStatuses, setPauseStatuses] = useState(['waiting_on_customer', 'under_investigation', 'resolved']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSla = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/sla`);
      if (data?.minutes_by_priority) {
        const next = {};
        for (const p of ['critical', 'high', 'medium', 'low']) {
          next[p] = minutesToDhm(data.minutes_by_priority[p]);
        }
        setDurations(next);
      }
      if (data?.pause_statuses) setPauseStatuses(data.pause_statuses);
      if (data?.defaults?.minutes_by_priority) setDefaults(data.defaults.minutes_by_priority);
    } catch (e) {
      toast.error('Failed to load SLA settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSla(); }, []);

  const setField = (priority, field, val) => {
    setDurations((cur) => ({
      ...cur,
      [priority]: { ...cur[priority], [field]: val === '' ? 0 : Math.max(0, parseInt(val, 10) || 0) },
    }));
  };

  const togglePauseStatus = (status) => {
    setPauseStatuses((cur) =>
      cur.includes(status) ? cur.filter((s) => s !== status) : [...cur, status]
    );
  };

  const save = async () => {
    const payload = {};
    for (const p of PRIORITIES) {
      const mins = dhmToMinutes(durations[p.key]);
      if (mins <= 0) {
        toast.error(`${p.label} duration must be greater than zero`);
        return;
      }
      payload[p.key] = mins;
    }
    payload.pause_statuses = pauseStatuses;

    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/settings/sla`, payload);
      if (data?.minutes_by_priority) {
        const next = {};
        for (const p of ['critical', 'high', 'medium', 'low']) {
          next[p] = minutesToDhm(data.minutes_by_priority[p]);
        }
        setDurations(next);
      }
      if (data?.pause_statuses) setPauseStatuses(data.pause_statuses);
      toast.success('SLA settings updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save SLA settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/settings/sla/reset`);
      if (data?.minutes_by_priority) {
        const next = {};
        for (const p of ['critical', 'high', 'medium', 'low']) {
          next[p] = minutesToDhm(data.minutes_by_priority[p]);
        }
        setDurations(next);
      }
      if (data?.pause_statuses) setPauseStatuses(data.pause_statuses);
      toast.success('Reset to preset defaults');
    } catch (e) {
      toast.error('Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  const describeMinutes = (total) => {
    if (!total) return '—';
    const { days, hours, minutes } = minutesToDhm(total);
    const parts = [];
    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);
    return parts.join(' ') || '0m';
  };

  return (
    <Card data-testid="sla-settings-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-rose-100">
              <Clock className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                SLA Breach Thresholds
              </CardTitle>
              <CardDescription>
                How long until a ticket is flagged as SLA-breached. Pause the clock on specific statuses.
              </CardDescription>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={resetToDefaults}
            disabled={loading || saving}
            data-testid="sla-reset-btn"
            className="text-slate-600 hover:text-slate-900"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" />
            Reset to presets
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading SLA configuration…
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {PRIORITIES.map((p) => (
                <div
                  key={p.key}
                  className="rounded-lg border border-slate-200 p-3"
                  data-testid={`sla-row-${p.key}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Badge variant="outline" className={`${p.color} text-xs`}>
                      {p.label}
                    </Badge>
                    <span className="text-xs text-slate-500">{p.hint}</span>
                    <span className="ml-auto text-[11px] text-slate-400">
                      Preset: {describeMinutes(defaults[p.key])}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label htmlFor={`sla-${p.key}-days`} className="text-[11px] text-slate-500">Days</Label>
                      <Input
                        id={`sla-${p.key}-days`}
                        type="number"
                        min="0"
                        max="365"
                        value={durations[p.key].days}
                        onChange={(e) => setField(p.key, 'days', e.target.value)}
                        className="h-9"
                        data-testid={`sla-input-${p.key}-days`}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`sla-${p.key}-hours`} className="text-[11px] text-slate-500">Hours</Label>
                      <Input
                        id={`sla-${p.key}-hours`}
                        type="number"
                        min="0"
                        max="23"
                        value={durations[p.key].hours}
                        onChange={(e) => setField(p.key, 'hours', e.target.value)}
                        className="h-9"
                        data-testid={`sla-input-${p.key}-hours`}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`sla-${p.key}-minutes`} className="text-[11px] text-slate-500">Minutes</Label>
                      <Input
                        id={`sla-${p.key}-minutes`}
                        type="number"
                        min="0"
                        max="59"
                        value={durations[p.key].minutes}
                        onChange={(e) => setField(p.key, 'minutes', e.target.value)}
                        className="h-9"
                        data-testid={`sla-input-${p.key}-minutes`}
                      />
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1.5">
                    Total: {describeMinutes(dhmToMinutes(durations[p.key]))}
                  </div>
                </div>
              ))}
            </div>

            {/* Pause statuses */}
            <div className="mt-4 rounded-lg border border-slate-200 p-3" data-testid="sla-pause-block">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200 text-xs">
                  Pause clock
                </Badge>
                <span className="text-xs text-slate-500">
                  SLA will stop counting when ticket is in any of these statuses.
                </span>
              </div>
              <div className="flex flex-wrap gap-3">
                {STATUS_OPTIONS.map((s) => (
                  <label
                    key={s.key}
                    className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"
                    data-testid={`sla-pause-${s.key}`}
                  >
                    <Checkbox
                      checked={pauseStatuses.includes(s.key)}
                      onCheckedChange={() => togglePauseStatus(s.key)}
                    />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
              <Button
                onClick={save}
                disabled={saving || loading}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="sla-save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save SLA settings
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Ticket Modules Settings Component
const TicketModulesSettings = () => {
  const [modules, setModules] = useState([]);
  const [newModule, setNewModule] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/settings/ticket-modules`);
      setModules(response.data.modules || []);
    } catch (error) {
      console.error('Failed to fetch modules:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModules();
  }, []);

  const handleAddModule = () => {
    if (!newModule.trim()) {
      toast.error('Module name cannot be empty');
      return;
    }
    if (modules.includes(newModule.trim())) {
      toast.error('Module already exists');
      return;
    }
    setModules([...modules, newModule.trim()]);
    setNewModule('');
  };

  const handleRemoveModule = (moduleToRemove) => {
    setModules(modules.filter(m => m !== moduleToRemove));
  };

  const handleSave = async () => {
    if (modules.length === 0) {
      toast.error('At least one module is required');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/settings/ticket-modules`, { modules });
      toast.success('Ticket modules updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update modules');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="ticket-modules-settings-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-100">
            <Ticket className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Support Ticket Modules
            </CardTitle>
            <CardDescription>
              Manage available modules for support ticket creation
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {/* Module List */}
            <div className="space-y-2">
              <Label className="text-sm text-slate-600">Available Modules</Label>
              <div className="flex flex-wrap gap-2">
                {modules.map((module) => (
                  <Badge
                    key={module}
                    variant="secondary"
                    className="px-3 py-1.5 flex items-center gap-2"
                  >
                    {module}
                    <button
                      onClick={() => handleRemoveModule(module)}
                      className="hover:text-red-600 transition-colors"
                      title="Remove module"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Add New Module */}
            <div className="flex gap-2">
              <Input
                value={newModule}
                onChange={(e) => setNewModule(e.target.value)}
                placeholder="Enter new module name"
                onKeyDown={(e) => e.key === 'Enter' && handleAddModule()}
              />
              <Button onClick={handleAddModule} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>

            {/* Save Button */}
            <div className="pt-4 border-t border-slate-200">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Modules
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};


// === Snippets settings (reply snippets + new-ticket templates) ===
const SnippetsSettings = () => {
  const [items, setItems] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, type, title, body, module}
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const [snipRes, modRes] = await Promise.all([
        axios.get(`${API}/snippets`),
        axios.get(`${API}/settings/ticket-modules`).catch(() => ({ data: { modules: [] } })),
      ]);
      setItems(snipRes.data || []);
      setModules((modRes.data && modRes.data.modules) || []);
    } catch {
      toast.error('Failed to load snippets');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchItems(); }, []);

  const startNew = (type) => {
    setEditing({ type, title: '', body: '', module: '' });
  };

  const handleSave = async () => {
    if (!editing) return;
    const t = (editing.title || '').trim();
    const b = (editing.body || '').trim();
    if (!t || !b) {
      toast.error('Title and body are required');
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/snippets/${editing.id}`, {
          title: t, body: b, module: editing.module || null,
        });
        toast.success('Snippet updated');
      } else {
        await axios.post(`${API}/snippets`, {
          type: editing.type, title: t, body: b, module: editing.module || null,
        });
        toast.success('Snippet created');
      }
      setEditing(null);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this snippet?')) return;
    try {
      await axios.delete(`${API}/snippets/${id}`);
      toast.success('Snippet deleted');
      fetchItems();
    } catch {
      toast.error('Delete failed');
    }
  };

  const renderList = (type, title, description, ctaLabel) => {
    const filtered = items.filter((s) => s.type === type);
    return (
      <div className="space-y-3 rounded-lg border border-slate-200 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            <div className="text-xs text-slate-500">{description}</div>
          </div>
          <Button size="sm" onClick={() => startNew(type)} data-testid={`new-snippet-${type}`}>
            <Plus className="w-4 h-4 mr-1" />
            {ctaLabel}
          </Button>
        </div>
        {filtered.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-4 text-center">No snippets yet.</div>
        ) : (
          <div className="space-y-2">
            {filtered.map((s) => (
              <div key={s.id} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`snippet-row-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{s.title}</div>
                    <div className="text-[11px] text-slate-500">
                      {s.module ? `Scoped to module: ${s.module}` : 'Any module'}
                    </div>
                    <div className="mt-1 text-xs text-slate-600 line-clamp-2">
                      {(s.body || '').replace(/<[^>]+>/g, ' ').trim().slice(0, 200)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => setEditing({ ...s })} className="h-8 w-8" data-testid={`edit-snippet-${s.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(s.id)} className="h-8 w-8 text-rose-600 hover:bg-rose-50" data-testid={`delete-snippet-${s.id}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card data-testid="snippets-settings-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-100">
            <StickyNote className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Snippet templates
            </CardTitle>
            <CardDescription>
              Canned reply text and new-ticket templates. Reply snippets show up in the composer; ticket templates show up as &ldquo;Use template&rdquo; in the New Ticket dialog.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            {renderList('reply', 'Reply snippets', 'Inserted into the reply composer. Supports {{ticket_number}}, {{customer_name}}, {{customer_email}}, {{location_name}}, {{module}}, {{user_name}} placeholders.', 'New reply snippet')}
            {renderList('ticket_template', 'New-ticket templates', 'Pre-fills the description on the Create Ticket dialog so common ticket types start with the right sections (e.g. Bug report, Feature request).', 'New ticket template')}
          </>
        )}

        {/* Edit / Create dialog */}
        {editing && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="snippet-editor">
              <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="text-sm font-semibold">
                  {editing.id ? 'Edit' : 'New'} {editing.type === 'reply' ? 'reply snippet' : 'ticket template'}
                </div>
                <Button variant="ghost" size="icon" onClick={() => setEditing(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="px-5 py-4 space-y-3">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    placeholder="e.g. Acknowledge + request screenshots"
                    data-testid="snippet-title"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Module (optional)</Label>
                  <Select
                    value={editing.module || '__none__'}
                    onValueChange={(v) => setEditing({ ...editing, module: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="snippet-module">
                      <SelectValue placeholder="Any module" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Any module</SelectItem>
                      {modules.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Body</Label>
                  <div className="mt-1" data-testid="snippet-body">
                    <RichTextEditor
                      content={editing.body || ''}
                      onChange={(html) => setEditing({ ...editing, body: html })}
                      placeholder={editing.type === 'reply'
                        ? 'Hi {{customer_name}},\n\nThanks for reaching out on #{{ticket_number}}…'
                        : 'Use bold for section headings, bullets/numbered lists for steps, tables for compatibility matrices.'}
                      minHeight={220}
                      enableTable
                    />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Available variables: <code>{`{{ticket_number}} {{customer_name}} {{customer_email}} {{location_name}} {{module}} {{user_name}}`}</code>
                  </div>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} data-testid="snippet-save">
                  {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// === Embed Widget settings (global, single token across all locations) ===
const EmbedSettings = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);

  const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const fetchInfo = async () => {
    try {
      const { data } = await axios.get(`${API}/public/embed/global`);
      setInfo(data);
    } catch {
      toast.error('Failed to load embed settings');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchInfo(); }, []);

  const generateToken = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/embed/global/token`);
      setInfo(data);
      toast.success('Embed token generated');
    } catch {
      toast.error('Failed to generate token');
    } finally {
      setBusy(false);
    }
  };

  const toggleEnabled = async (enabled) => {
    setBusy(true);
    try {
      await axios.put(`${API}/public/embed/global/toggle`, { enabled });
      setInfo({ ...info, embed_enabled: enabled });
      toast.success(enabled ? 'Embed enabled' : 'Embed disabled');
    } catch {
      toast.error('Failed to update');
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <Card data-testid="embed-settings-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-100">
              <Code2 className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Embed widget
              </CardTitle>
              <CardDescription>
                One global form that external websites can drop in. Submitters pick the location from a dropdown.
              </CardDescription>
            </div>
          </div>
          {info?.embed_enabled ? (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
              <CheckCircle className="w-3 h-3 mr-1" />
              Live
            </Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-700 border-slate-200">
              Disabled
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : info && info.embed_token ? (
          <div className="space-y-5">
            {/* Status row */}
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <div>
                <div className="text-sm font-medium text-slate-900">Status</div>
                <div className="text-xs text-slate-500">
                  {info.embed_enabled
                    ? 'External submissions are accepted across all locations.'
                    : 'External submissions are rejected (form shows a friendly message).'}
                </div>
              </div>
              <Button
                variant={info.embed_enabled ? 'outline' : 'default'}
                size="sm"
                disabled={busy}
                onClick={() => toggleEnabled(!info.embed_enabled)}
                className={info.embed_enabled ? '' : 'bg-emerald-600 hover:bg-emerald-700'}
                data-testid="embed-toggle-btn"
              >
                {info.embed_enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>

            {/* Token */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Embed token</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  value={info.embed_token}
                  readOnly
                  className="font-mono text-xs"
                  data-testid="embed-token-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copy(info.embed_token, 'Token copied')}
                  data-testid="copy-embed-token"
                  title="Copy token"
                >
                  <Copy className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={generateToken}
                  disabled={busy}
                  title="Rotate (regenerate) the token — the old token stops working"
                  data-testid="regen-embed-token"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Anyone with this token can submit tickets. Rotate it if it leaks.
              </div>
            </div>

            {(() => {
              const widgetSnippet = `<script src="${baseUrl}/api/public/embed/widget.js" defer\n        data-token="${info.embed_token}"\n        data-color="#4f46e5"\n        data-label="Get Support"><\/script>`;
              const iframeSnippet = `<iframe src="${baseUrl}/api/public/embed/form?token=${info.embed_token}"\n        width="560" height="680" frameborder="0" style="border:1px solid #e2e8f0; border-radius:12px;"><\/iframe>`;
              const previewUrl = `${baseUrl}/api/public/embed/form?token=${info.embed_token}`;
              return (
                <>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Snippet 1 — Floating &quot;+&quot; button (recommended)
                    </Label>
                    <div className="mt-1 relative">
                      <pre className="bg-slate-900 text-slate-100 text-xs p-3 pr-12 rounded-lg overflow-x-auto whitespace-pre font-mono" data-testid="embed-snippet-widget">
{widgetSnippet}
                      </pre>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 h-7 w-7 text-slate-300 hover:text-white hover:bg-slate-800"
                        onClick={() => copy(widgetSnippet, 'Snippet copied')}
                        data-testid="copy-embed-snippet"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      Paste this before the closing <code>&lt;/body&gt;</code> tag on your website.
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Snippet 2 — Inline iframe (alternative)
                    </Label>
                    <div className="mt-1 relative">
                      <pre className="bg-slate-900 text-slate-100 text-xs p-3 pr-12 rounded-lg overflow-x-auto whitespace-pre font-mono" data-testid="embed-snippet-iframe">
{iframeSnippet}
                      </pre>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 h-7 w-7 text-slate-300 hover:text-white hover:bg-slate-800"
                        onClick={() => copy(iframeSnippet, 'Snippet copied')}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3">
                    <div className="text-sm text-indigo-900">
                      <strong>Preview:</strong> open the form in a new tab.
                    </div>
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-indigo-700 hover:text-indigo-900"
                      data-testid="embed-preview-link"
                    >
                      Open preview →
                    </a>
                  </div>
                </>
              );
            })()}
          </div>
        ) : (
          <div className="py-8 text-center space-y-4">
            <div className="text-sm text-slate-500">
              No embed token has been generated yet. Generate one to start accepting submissions from external websites.
            </div>
            <Button onClick={generateToken} disabled={busy} data-testid="generate-embed-token-btn">
              <Code2 className="w-4 h-4 mr-2" />
              Generate embed token
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};



const SettingsPage = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  
  const [settings, setSettings] = useState({
    support_email: '',
    sender_email: 'onboarding@resend.dev',
    email_signature: '',
    public_app_url: '',
    notification_recipients: [],
    notify_on_tickets: true,
    notify_on_sync_request: true,
    notify_on_completion: true,
    notify_on_offboarding: true
  });
  
  const [emailConfigured, setEmailConfigured] = useState(false);
  
  // For adding new recipients
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [newRecipientName, setNewRecipientName] = useState('');

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings/email`);
      setSettings(response.data.settings);
      setEmailConfigured(response.data.configured);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // Use defaults if endpoint doesn't exist yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/email`, settings);
      toast.success('Settings saved successfully');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!settings.support_email) {
      toast.error('Please enter a support email first');
      return;
    }
    
    setTestingEmail(true);
    try {
      await axios.post(`${API}/settings/email/test`, {
        to_email: settings.support_email
      });
      toast.success('Test email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const addRecipient = () => {
    if (!newRecipientEmail || !newRecipientName) {
      toast.error('Please enter both email and name');
      return;
    }
    
    // Ensure notification_recipients is an array
    const currentRecipients = settings.notification_recipients || [];
    
    // Check if email already exists
    if (currentRecipients.some(r => r.email === newRecipientEmail)) {
      toast.error('This email is already added');
      return;
    }
    
    const newRecipient = {
      email: newRecipientEmail,
      name: newRecipientName,
      notify_tickets: true,
      notify_sync_requests: true,
      notify_offboarding: true
    };
    
    setSettings({
      ...settings,
      notification_recipients: [...currentRecipients, newRecipient]
    });
    
    setNewRecipientEmail('');
    setNewRecipientName('');
    toast.success('Recipient added');
  };

  const removeRecipient = (index) => {
    const updated = settings.notification_recipients.filter((_, i) => i !== index);
    setSettings({ ...settings, notification_recipients: updated });
    toast.success('Recipient removed');
  };

  const updateRecipient = (index, field, value) => {
    const updated = [...settings.notification_recipients];
    updated[index] = { ...updated[index], [field]: value };
    setSettings({ ...settings, notification_recipients: updated });
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
          <h2 className="text-xl font-semibold text-slate-800">Access Restricted</h2>
          <p className="text-slate-600 mt-2">This page is only accessible to administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Settings
          </h1>
          <p className="text-slate-500 mt-1">Configure email notifications and system settings</p>
        </div>
      </div>

      <Tabs defaultValue="email" className="w-full" data-testid="settings-tabs">
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="inline-flex w-auto md:grid md:w-full md:max-w-3xl md:grid-cols-7">
            <TabsTrigger value="email" data-testid="tab-email">
              <Mail className="w-4 h-4 mr-2" />
              Email
            </TabsTrigger>
            <TabsTrigger value="modules" data-testid="tab-modules">
              <Ticket className="w-4 h-4 mr-2" />
              Modules
            </TabsTrigger>
            <TabsTrigger value="sla" data-testid="tab-sla">
              <Clock className="w-4 h-4 mr-2" />
              SLA
            </TabsTrigger>
            <TabsTrigger value="internal-status" data-testid="tab-internal-status">
              <ListChecks className="w-4 h-4 mr-2" />
              Internal Status
            </TabsTrigger>
            <TabsTrigger value="snippets" data-testid="tab-snippets">
              <StickyNote className="w-4 h-4 mr-2" />
              Snippets
            </TabsTrigger>
            <TabsTrigger value="embed" data-testid="tab-embed">
              <Code2 className="w-4 h-4 mr-2" />
              Embed
            </TabsTrigger>
            <TabsTrigger value="webhook" data-testid="tab-webhook">
              <RefreshCw className="w-4 h-4 mr-2" />
              Webhook
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="email" className="mt-4" data-testid="tab-panel-email">
      {/* Email Notifications Card */}
      <Card data-testid="email-settings-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-100">
                <Mail className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Email Notifications
                </CardTitle>
                <CardDescription>
                  Configure email alerts for sync requests
                </CardDescription>
              </div>
            </div>
            {emailConfigured ? (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                <CheckCircle className="w-3 h-3 mr-1" />
                Configured
              </Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                <AlertCircle className="w-3 h-3 mr-1" />
                Not Configured
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              <span className="ml-2 text-slate-600">Loading settings...</span>
            </div>
          ) : (
            <>
              {/* Info Banner */}
              {!emailConfigured && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-900">Email API Key Required</p>
                      <p className="text-sm text-amber-700 mt-1">
                        To enable email notifications, add your Resend API key to the backend environment variables:
                      </p>
                      <code className="block mt-2 text-xs bg-amber-100 p-2 rounded text-amber-800">
                        RESEND_API_KEY=re_your_api_key_here
                      </code>
                    </div>
                  </div>
                </div>
              )}

              {/* Support Email */}
              <div className="space-y-2">
                <Label htmlFor="support_email" className="text-sm font-medium text-slate-700">
                  Support Email (Reply-To Address)
                </Label>
                <Input
                  id="support_email"
                  type="email"
                  placeholder="support@yourcompany.com"
                  value={settings.support_email}
                  onChange={(e) => setSettings({ ...settings, support_email: e.target.value })}
                  data-testid="support-email-input"
                />
                <p className="text-xs text-slate-500">
                  ✉️ Customers will see and reply to this email address (hides individual admin emails)
                </p>
              </div>

              {/* Sender Email */}
              <div className="space-y-2">
                <Label htmlFor="sender_email">Sender Email</Label>
                <Input
                  id="sender_email"
                  type="email"
                  placeholder="notifications@yourcompany.com"
                  value={settings.sender_email}
                  onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                  data-testid="sender-email-input"
                />
                <p className="text-xs text-slate-500">
                  Email address notifications will be sent from (must be verified in Resend)
                </p>
              </div>

              {/* Public App URL */}
              {(() => {
                const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '').toLowerCase();
                const publicUrl  = (settings.public_app_url || '').replace(/\/$/, '').toLowerCase();
                const isSet = !!publicUrl;
                const mismatch = isSet && backendUrl && publicUrl !== backendUrl;
                return (
                  <div className={`space-y-2 rounded-md border p-3 ${
                    mismatch ? 'border-rose-300 bg-rose-50' : (!isSet ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50')
                  }`}>
                    <Label htmlFor="public_app_url" className="flex items-center gap-2 flex-wrap">
                      Public App URL (whitelabel)
                      {!isSet && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                          Not set — emails will fall back to backend URL
                        </Badge>
                      )}
                      {isSet && !mismatch && (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                          Active
                        </Badge>
                      )}
                      {mismatch && (
                        <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px]">
                          Doesn&apos;t match this deployment — images / links will break
                        </Badge>
                      )}
                    </Label>
                    <Input
                      id="public_app_url"
                      type="url"
                      placeholder="https://app.yourcompany.com"
                      value={settings.public_app_url || ''}
                      onChange={(e) => setSettings({ ...settings, public_app_url: e.target.value })}
                      data-testid="public-app-url-input"
                      className="bg-white"
                    />
                    <p className="text-xs text-slate-700">
                      All &quot;View Ticket / View Conversation&quot; buttons in notification emails, and image URLs baked into replies,
                      link to this domain. Set it to whatever domain your users actually visit.
                    </p>
                    {backendUrl && (
                      <p className="text-[11px] text-slate-500">
                        This deployment&apos;s backend URL: <code>{backendUrl}</code>
                        {mismatch && (
                          <> — <button
                            type="button"
                            onClick={() => setSettings({ ...settings, public_app_url: backendUrl })}
                            className="text-rose-700 underline font-medium hover:text-rose-900"
                            data-testid="fix-public-app-url"
                          >Use this URL</button></>
                        )}
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Email Signature */}
              <div className="space-y-2">
                <Label htmlFor="email_signature" className="text-sm font-medium text-slate-700">
                  Email Signature
                </Label>
                <Textarea
                  id="email_signature"
                  placeholder="Best regards,&#10;Your Support Team&#10;support@yourcompany.com"
                  value={settings.email_signature}
                  onChange={(e) => setSettings({ ...settings, email_signature: e.target.value })}
                  className="max-w-2xl min-h-[100px]"
                  data-testid="email-signature-input"
                />
                <p className="text-xs text-slate-500">
                  This signature will appear at the end of all outgoing notification emails
                </p>
              </div>

              {/* Notification Recipients */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-600" />
                  <Label className="text-sm font-medium text-slate-700">
                    Notification Recipients
                  </Label>
                </div>
                <p className="text-xs text-slate-500">
                  Configure who receives notifications for different event types
                </p>

                {/* Recipients List */}
                <div className="space-y-2">
                  {settings.notification_recipients && settings.notification_recipients.length > 0 ? (
                    settings.notification_recipients.map((recipient, index) => (
                      <div 
                        key={recipient.email || `recipient-${index}`}
                        className="flex items-center gap-4 p-3 border border-slate-200 rounded-lg bg-slate-50"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{recipient.name}</p>
                          <p className="text-sm text-slate-500 truncate">{recipient.email}</p>
                        </div>
                        <div className="flex gap-3 items-center">
                          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                            <Checkbox
                              checked={recipient.notify_tickets}
                              onCheckedChange={(checked) => updateRecipient(index, 'notify_tickets', checked)}
                            />
                            <span className="text-slate-600">Tickets</span>
                          </label>
                          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                            <Checkbox
                              checked={recipient.notify_sync_requests}
                              onCheckedChange={(checked) => updateRecipient(index, 'notify_sync_requests', checked)}
                            />
                            <span className="text-slate-600">Sync</span>
                          </label>
                          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                            <Checkbox
                              checked={recipient.notify_offboarding}
                              onCheckedChange={(checked) => updateRecipient(index, 'notify_offboarding', checked)}
                            />
                            <span className="text-slate-600">Offboard</span>
                          </label>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeRecipient(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-center border border-dashed border-slate-300 rounded-lg">
                      <p className="text-sm text-slate-500">No recipients added yet</p>
                    </div>
                  )}
                </div>

                {/* Add Recipient Form */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Email address"
                    type="email"
                    value={newRecipientEmail}
                    onChange={(e) => setNewRecipientEmail(e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Name"
                    value={newRecipientName}
                    onChange={(e) => setNewRecipientName(e.target.value)}
                    className="flex-1"
                  />
                  <Button 
                    onClick={addRecipient}
                    variant="outline"
                    className="shrink-0"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>

              {/* Notification Toggles */}
              <div className="space-y-4 pt-4 border-t border-slate-200">
                <h3 className="font-medium text-slate-700 flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Notification Preferences
                </h3>
                
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-700">New Ticket Notifications</p>
                    <p className="text-sm text-slate-500">Notify when a new support ticket is created</p>
                  </div>
                  <Switch
                    checked={settings.notify_on_tickets ?? true}
                    onCheckedChange={(checked) => setSettings({ ...settings, notify_on_tickets: checked })}
                    data-testid="notify-tickets-toggle"
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-700">New Sync Request</p>
                    <p className="text-sm text-slate-500">Notify when a client submits a sync request</p>
                  </div>
                  <Switch
                    checked={settings.notify_on_sync_request}
                    onCheckedChange={(checked) => setSettings({ ...settings, notify_on_sync_request: checked })}
                    data-testid="notify-sync-request-toggle"
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-700">Request Completed</p>
                    <p className="text-sm text-slate-500">Notify user when their sync request is completed</p>
                  </div>
                  <Switch
                    checked={settings.notify_on_completion}
                    onCheckedChange={(checked) => setSettings({ ...settings, notify_on_completion: checked })}
                    data-testid="notify-completion-toggle"
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-700">Offboarding Request Notifications</p>
                    <p className="text-sm text-slate-500">Notify when a new offboarding request is submitted</p>
                  </div>
                  <Switch
                    checked={settings.notify_on_offboarding ?? true}
                    onCheckedChange={(checked) => setSettings({ ...settings, notify_on_offboarding: checked })}
                    data-testid="notify-offboarding-toggle"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-settings-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={handleTestEmail}
                  disabled={testingEmail || !emailConfigured || !settings.support_email}
                  data-testid="test-email-btn"
                >
                  {testingEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Test Email
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="modules" className="mt-4" data-testid="tab-panel-modules">
          {/* Ticket Modules Configuration Card */}
          <TicketModulesSettings />
        </TabsContent>

        <TabsContent value="sla" className="mt-4" data-testid="tab-panel-sla">
          {/* SLA Threshold Settings Card */}
          <SlaSettings />
        </TabsContent>

        <TabsContent value="internal-status" className="mt-4" data-testid="tab-panel-internal-status">
          <InternalStatusSettings />
        </TabsContent>

        <TabsContent value="snippets" className="mt-4" data-testid="tab-panel-snippets">
          <SnippetsSettings />
        </TabsContent>

        <TabsContent value="embed" className="mt-4" data-testid="tab-panel-embed">
          <EmbedSettings />
        </TabsContent>

        <TabsContent value="webhook" className="mt-4" data-testid="tab-panel-webhook">
      {/* Webhook Settings Card */}
      <Card data-testid="webhook-settings-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100">
              <RefreshCw className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Webhook Configuration
              </CardTitle>
              <CardDescription>
                Configure your CRM webhook URL for real-time sync
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <Label className="text-sm text-slate-500 mb-2 block">Webhook URL</Label>
            <code className="block text-sm bg-white p-3 rounded border border-slate-200 text-slate-800 break-all">
              {process.env.REACT_APP_BACKEND_URL}/api/webhooks/crm
            </code>
            <p className="text-xs text-slate-500 mt-2">
              Add this URL in your CRM webhook settings to receive real-time updates
            </p>
          </div>
          
          <div className="text-sm text-slate-600">
            <p className="font-medium mb-2">Supported Events:</p>
            <ul className="list-disc list-inside space-y-1 text-slate-500">
              <li>Custom Field changes (create, update, delete)</li>
              <li>User changes (create, update, delete)</li>
              <li>Calendar changes (create, update, delete)</li>
              <li>Contact changes (logged only)</li>
            </ul>
          </div>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
